import { Heading, useDisclosure } from '@chakra-ui/react';
import { IPaymentMethodResp } from '@monetize/types/app';
import {
  DEFAULT_PAGER,
  FilterType,
  TDataTablePager,
} from '@monetize/types/common';
import { FC, useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useGetGatewayAccountsByAccount,
  useGetPaymentMethodsByAccount,
} from '../../../api/accountsService';
import {
  MBox,
  MFlex,
  MHStack,
  MPageHeader,
  MPageSearchInput,
} from '../../../components/Monetize';
import { useTableColumns } from '../../../components/Monetize/DataTableNew/columnCatalog/useTableColumns';
import { DataTable } from '../../../components/Monetize/DataTableNew/DataTable';
import { ACTION_COLUMN_KEY } from '../../../components/Monetize/DataTableNew/dataTableNewRenderers';
import { DataTableRef } from '../../../components/Monetize/DataTableNew/DataTableTypes';
import { ExportEntityButton } from '../../../components/Monetize/ExportEntityButton';
import { RefreshTableButton } from '../../../components/Monetize/RefreshTableButton';
import {
  getAccountPaymentMethodCreate,
  getAccountPaymentMethodView,
} from '../../../constants/routes';
import useCurrentPaymentGateways from '../../../hooks/useCurrentPaymentGateways';
import { useACL } from '../../../services/acl/acl';
import { ACLCheck } from '../../../services/acl/ACLCheck';
import {
  buildFilterParamsRequestObject,
  getIsTrulyEmptyList,
} from '../../../utils';
import { navigateOrOpenNewWindow } from '../../../utils/misc';
import { GatewayAccountButton } from './GatewayAccountButton';
import { GatewayAccountLinkButton } from './GatewayAccountLinkButton';
import { PaymentMethodActions } from './PaymentMethodActions';
import { PaymentMethodListActions } from './PaymentMethodListActions';
import { PaymentMethodShareLinkModal } from './PaymentMethodShareLinkModal';
import {
  PAYMENT_METHOD_LIST_CATALOG,
  PaymentMethodListColumnContext,
} from './paymentMethodList.columns';

export const usePaymentMethodsData = (accountId: string) => {
  const [filters, setFilters] = useState<FilterType[]>([]);
  const [searchKey] = useState<string>('paymentMethodName');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [pager, setPager] = useState<TDataTablePager>(DEFAULT_PAGER);
  const filterParams = useMemo(
    () => buildFilterParamsRequestObject(filters, searchTerm, searchKey),
    [filters, searchTerm, searchKey],
  );

  const {
    open: isModalOpen,
    onOpen: onOpenModal,
    onClose: onCloseModal,
  } = useDisclosure();

  const {
    isLoading,
    isRefetching,
    isFetched,
    data: listData,
    isError,
    error,
    refetch,
  } = useGetPaymentMethodsByAccount(
    {
      accountId,
      config: pager,
      filters: filterParams,
    },
    { enabled: !!accountId },
  );

  const { data: gatewayAccountsData, isLoading: gatewayAccountsDataIsLoading } =
    useGetGatewayAccountsByAccount(accountId, {
      enabled: !!accountId, // Executes only when accountId is truthy
    });

  const [selected, setSelected] = useState<null | IPaymentMethodResp>(null);

  const openCreateModal = () => {
    onOpenModal();
  };

  const openEditModal = (row: IPaymentMethodResp) => {
    setSelected(row);
    onOpenModal();
  };

  const onResetFilter = () => {
    setFilters([]);
    setPager(DEFAULT_PAGER);
  };

  return {
    pager,
    setPager,
    filters,
    setFilters,
    searchKey,
    searchTerm,
    setSearchTerm,
    onResetFilter,
    totalRecords: listData?.totalElements || 0,
    loading: isLoading || (isRefetching && !isFetched),
    isModalOpen,
    onOpenModal,
    onCloseModal: () => {
      setSelected(null);
      onCloseModal();
    },
    openCreateModal,
    openEditModal,
    listData,
    selected,
    setSelected,
    gatewayAccountsData,
    gatewayAccountsDataIsLoading,
    isError,
    error,
    refetch,
  };
};

export const PaymentMethodList: FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const accountId = params?.accountId || '';
  const {
    filters,
    setFilters,
    searchKey,
    searchTerm,
    setSearchTerm,
    onResetFilter,
    listData,
    pager,
    setPager,
    loading,
    gatewayAccountsData,
    gatewayAccountsDataIsLoading,
    refetch,
  } = usePaymentMethodsData(accountId);
  const { canDo } = useACL();
  const { currentPaymentGateway: paymentGateway } = useCurrentPaymentGateways();
  const tableRef = useRef<DataTableRef>(null);

  const actionBodyTemplate = useCallback(
    (rowData: IPaymentMethodResp) => <PaymentMethodActions data={rowData} />,
    [],
  );

  const columnContext = useMemo<PaymentMethodListColumnContext>(
    () => ({ accountId, actionBodyTemplate }),
    [accountId, actionBodyTemplate],
  );
  const { columns, state: columnState } = useTableColumns(
    PAYMENT_METHOD_LIST_CATALOG,
    columnContext,
  );

  const isTrulyEmptyList = getIsTrulyEmptyList({
    loading,
    totalElements: listData?.totalElements || 0,
    filters,
    searchTerm,
    page: pager.page,
  });

  const handleResetFilter = () => {
    onResetFilter();
    tableRef.current?.resetFilters();
  };

  const firstGatewayAccountData = gatewayAccountsData && gatewayAccountsData[0];

  return (
    <MBox>
      <MPageHeader
        title={
          <MFlex>
            <Heading size="lg" color="tPurple.base" lineClamp={2} mr={2}>
              Payment Method
            </Heading>

            <GatewayAccountButton
              gatewayCustomerId={firstGatewayAccountData?.gatewayCustomerId}
              gatewayAccountsDataIsLoading={gatewayAccountsDataIsLoading}
              accountId={accountId}
              buttonProps={{
                size: 'sm',
                variant: 'tertiary',
              }}
            />
          </MFlex>
        }
        hideContent={isTrulyEmptyList}
      >
        <MFlex>
          <MHStack gap="2">
            <MPageSearchInput
              placeholderKey="Name"
              value={searchTerm || ''}
              onChange={(e: any) => setSearchTerm && setSearchTerm(e)}
              count={listData?.totalElements}
              mode="alwaysExpanded"
            />
            <RefreshTableButton refetch={refetch} />
            <ExportEntityButton
              entity="paymentMethods"
              filters={filters}
              searchKey={searchKey}
              searchTerm={searchTerm}
              sortField={pager.sortField}
              sortOrder={pager.sortOrder}
              endpointParams={[accountId]}
              getFilename={() => `payment-methods-${accountId}.csv`}
            />
            {paymentGateway && (
              <ACLCheck acls={[['billing', 'create']]}>
                <PaymentMethodShareLinkModal
                  accountId={accountId}
                  paymentGateway={paymentGateway}
                />

                <PaymentMethodListActions
                  accountId={accountId}
                  gatewayCustomerId={firstGatewayAccountData?.gatewayCustomerId}
                />
              </ACLCheck>
            )}
          </MHStack>
        </MFlex>
      </MPageHeader>
      <DataTable
        ref={tableRef}
        columns={columns}
        state={columnState}
        data={listData?.content ?? []}
        pinnedRightColumns={[ACTION_COLUMN_KEY]}
        totalElements={listData?.totalElements}
        totalPages={listData?.totalPages}
        pager={pager}
        setPager={setPager}
        initialSortingState={[{ id: 'createDate', desc: true }]}
        initialFilters={filters}
        onFiltersChange={(newFilters) => {
          setFilters(newFilters);
        }}
        onRowClick={(row, _tableRow, openInNewWindow) => {
          if (!row.id) {
            return;
          }
          return navigateOrOpenNewWindow(
            navigate,
            getAccountPaymentMethodView(accountId, row.id),
            openInNewWindow,
          );
        }}
        isLoading={loading}
        emptyProps={{
          mainMessage: 'Looks like there are no payment methods here.',
          smallMessage:
            'You can automate your payments by adding a payment method.',
          btnLabel:
            canDo([['billing', 'create']]) && paymentGateway
              ? 'New Payment Method'
              : '',
          to: getAccountPaymentMethodCreate(accountId),
          childBtnContent: paymentGateway ? (
            <>
              <PaymentMethodShareLinkModal
                accountId={accountId}
                paymentGateway={paymentGateway}
                buttonProps={{ mt: 2 }}
              />
              <GatewayAccountButton
                gatewayCustomerId={firstGatewayAccountData?.gatewayCustomerId}
                gatewayAccountsDataIsLoading={gatewayAccountsDataIsLoading}
                accountId={accountId}
                buttonProps={{
                  size: 'sm',
                  variant: 'secondary',
                  mt: 2,
                }}
              />
              {!firstGatewayAccountData && (
                <GatewayAccountLinkButton
                  accountId={accountId}
                  paymentGatewayId={paymentGateway.id}
                  buttonProps={{
                    size: 'sm',
                    variant: 'secondary',
                    mt: 2,
                  }}
                />
              )}
            </>
          ) : undefined,
        }}
        emptyFilterResultsProps={{
          btnLabel: 'Reset Filters',
        }}
        resetFilter={handleResetFilter}
      />
    </MBox>
  );
};
